import React, { useState, useEffect } from 'react';
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonButton,
  IonIcon,
  IonChip,
  IonLabel,
  IonSpinner,
  IonAlert,
  IonToast,
  IonList,
  IonItem,
  IonItemSliding,
  IonItemOptions,
  IonItemOption,
} from '@ionic/react';
import { 
  logoGoogle, 
  mailOutline, 
  checkmarkCircle, 
  closeCircle, 
  linkOutline, 
  unlinkOutline,
  addCircleOutline,
  trashOutline,
  refreshOutline,
  personCircleOutline
} from 'ionicons/icons';
import { googleAuthService } from '../services/googleAuth';
import { userService } from '../services/api';
import { useTranslation } from 'react-i18next';
import { eventBus, EVENTS } from '../utils/eventBus';

interface ConnectedAccount {
  email: string;
  connectedAt: string;
  lastSync: string;
  isActive: boolean;
}

const GoogleIntegration: React.FC = () => {
  const { t } = useTranslation();
  const [connectedAccounts, setConnectedAccounts] = useState<ConnectedAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDisconnectAlert, setShowDisconnectAlert] = useState(false);
  const [accountToDisconnect, setAccountToDisconnect] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastColor, setToastColor] = useState<'success' | 'danger'>('success');
  const [emails, setEmails] = useState<any[]>([]);
  const [fetchingEmails, setFetchingEmails] = useState(false);
  const [fetchingAccount, setFetchingAccount] = useState<string | null>(null);

  useEffect(() => {
    loadConnectedAccounts();
    // Only check for callback params once when component mounts
    checkForCallbackParams();
  }, []);

  // Add another useEffect to handle URL changes during navigation
  useEffect(() => {
    const handlePopState = () => {
      // If user navigates back/forward and there are success/error params, clean them up
      const params = new URLSearchParams(window.location.search);
      if (params.get('success') === 'google' || params.get('error') === 'google') {
        cleanUpUrlParams();
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const loadConnectedAccounts = async () => {
    setLoading(true);
    try {
      const result = await googleAuthService.getConnectedAccounts();
      console.log('Connected accounts result:', result);
      setConnectedAccounts(result.accounts || []);
    } catch (error) {
      console.error('Failed to load connected accounts:', error);
      setConnectedAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const checkForCallbackParams = async () => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('success');
    const error = params.get('error');

    if (success === 'google') {
      setToastMessage('Google account connected successfully!');
      setToastColor('success');
      setShowToast(true);
      // Clean up URL parameters more thoroughly
      cleanUpUrlParams();
      // Refresh connection status to get the email
      await loadConnectedAccounts();
    } else if (error === 'google') {
      setToastMessage('Failed to connect Google account. Please try again.');
      setToastColor('danger');
      setShowToast(true);
      // Clean up URL parameters more thoroughly
      cleanUpUrlParams();
    }
  };

  const cleanUpUrlParams = () => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('success');
      url.searchParams.delete('error');
      url.searchParams.delete('tab'); // Also clean up tab parameter if it was added
      
      // Use the cleaned URL
      const cleanedUrl = url.pathname + (url.search || '');
      window.history.replaceState({}, document.title, cleanedUrl);
    } catch (error) {
      console.warn('Failed to clean up URL parameters:', error);
      // Fallback to simple cleanup
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const handleConnect = () => {
    const authUrl = googleAuthService.getAuthUrl();
    if (!import.meta.env.VITE_GOOGLE_CLIENT_ID || import.meta.env.VITE_GOOGLE_CLIENT_ID === 'YOUR_GOOGLE_CLIENT_ID_HERE') {
      alert('Please set up Google OAuth credentials first. Check GOOGLE_OAUTH_SETUP.md for instructions.');
      return;
    }
    window.location.href = authUrl;
  };

  const handleFetchEmails = async (email?: string) => {
    try {
      setFetchingEmails(true);
      if (email) {
        setFetchingAccount(email);
      }
      
      const result = await googleAuthService.fetchRecentEmails(email);
      setEmails(result.emails || []);
      
      // Count how many sales were created
      const ticketEmails = result.emails?.filter((e: any) => e.isTicketRelated) || [];
      const processedEmails = ticketEmails.filter((e: any) => e.processed);
      
      if (processedEmails.length > 0) {
        setToastMessage(`Fetched ${result.count} emails ${email ? `from ${email}` : 'from all accounts'} and created ${processedEmails.length} sales automatically!`);
        // Emit event to refresh sales across the app
        eventBus.emit(EVENTS.SALES_CREATED_FROM_EMAIL, { count: processedEmails.length });
        eventBus.emit(EVENTS.REFRESH_SALES);
        eventBus.emit(EVENTS.REFRESH_TICKETS);
      } else if (ticketEmails.length > 0) {
        setToastMessage(`Fetched ${result.count} emails ${email ? `from ${email}` : 'from all accounts'}. Found ${ticketEmails.length} ticket-related emails.`);
      } else {
        setToastMessage(`Fetched ${result.count} recent emails ${email ? `from ${email}` : 'from all accounts'}`);
      }
      
      // Always emit emails fetched event
      eventBus.emit(EVENTS.EMAILS_FETCHED, { emails: result.emails, count: result.count });
      
      setToastColor('success');
      setShowToast(true);
    } catch (error: any) {
      console.error('Email fetch error:', error);
      
      // Check if it's a token expiry issue
      if (error.message?.includes('expired') || error.message?.includes('reconnect')) {
        setToastMessage(error.message);
        // Reload accounts to update their status
        loadConnectedAccounts();
      } else {
        setToastMessage(error.message || 'Failed to fetch emails');
      }
      setToastColor('danger');
      setShowToast(true);
    } finally {
      setFetchingEmails(false);
      setFetchingAccount(null);
    }
  };

  const handleDisconnect = async () => {
    try {
      setLoading(true);
      await googleAuthService.disconnect(accountToDisconnect || undefined);
      
      if (accountToDisconnect) {
        setToastMessage(`Disconnected ${accountToDisconnect}`);
      } else {
        setToastMessage('All Google accounts disconnected successfully');
        setEmails([]); // Clear emails when all accounts are disconnected
      }
      
      setToastColor('success');
      setShowToast(true);
      
      // Reload accounts
      await loadConnectedAccounts();
    } catch (error) {
      setToastMessage('Failed to disconnect Google account');
      setToastColor('danger');
      setShowToast(true);
    } finally {
      setLoading(false);
      setShowDisconnectAlert(false);
      setAccountToDisconnect(null);
    }
  };

  const confirmDisconnect = (email?: string) => {
    setAccountToDisconnect(email || null);
    setShowDisconnectAlert(true);
  };

  if (loading) {
    return (
      <IonCard>
        <IonCardContent className="text-center py-8">
          <IonSpinner />
        </IonCardContent>
      </IonCard>
    );
  }

  const hasAccounts = connectedAccounts.length > 0;

  return (
    <>
      <IonCard>
        <IonCardHeader>
          <IonCardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <IonIcon icon={logoGoogle} className="text-2xl" />
              <span>Google Integration</span>
            </div>
            {hasAccounts && (
              <IonChip color="primary">
                <IonLabel>{connectedAccounts.length} account{connectedAccounts.length !== 1 ? 's' : ''}</IonLabel>
              </IonChip>
            )}
          </IonCardTitle>
        </IonCardHeader>
        <IonCardContent>
          <div className="space-y-4">
            {!hasAccounts ? (
              <>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <h4 className="font-medium mb-2">What this integration offers:</h4>
                  <ul className="text-sm space-y-1 text-gray-600 dark:text-gray-400">
                    <li>• Connect multiple Gmail accounts</li>
                    <li>• Monitor emails for ticket purchase confirmations</li>
                    <li>• Automatically extract event information from emails</li>
                    <li>• Get notified when new tickets are detected</li>
                    <li>• Secure OAuth 2.0 authentication</li>
                  </ul>
                </div>

                <IonButton 
                  expand="block" 
                  color="primary"
                  onClick={handleConnect}
                >
                  <IonIcon icon={linkOutline} slot="start" />
                  Connect First Google Account
                </IonButton>
              </>
            ) : (
              <>
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-4">
                  <h4 className="font-medium mb-2 flex items-center">
                    <IonIcon icon={checkmarkCircle} className="text-blue-600 dark:text-blue-400 mr-2" />
                    Connected Accounts
                  </h4>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Monitoring {connectedAccounts.length} Gmail account{connectedAccounts.length !== 1 ? 's' : ''} for ticket emails
                  </p>
                </div>

                {/* Connected Accounts List */}
                <IonList className="mb-4">
                  {connectedAccounts.map((account) => (
                    <IonItemSliding key={account.email}>
                      <IonItem>
                        <IonIcon icon={personCircleOutline} slot="start" className="text-2xl" />
                        <IonLabel>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{account.email}</h3>
                            {!account.isActive && (
                              <IonChip color="warning" className="text-xs">
                                <IonLabel>{t('google.reAuthRequired', 'Re-auth needed')}</IonLabel>
                              </IonChip>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">
                            Connected {new Date(account.connectedAt).toLocaleDateString()}
                          </p>
                        </IonLabel>
                        <div slot="end" className="flex gap-1">
                          <IonButton 
                            fill="clear"
                            onClick={() => handleFetchEmails(account.email)}
                            disabled={fetchingEmails && fetchingAccount === account.email}
                          >
                            <IonIcon icon={refreshOutline} slot="icon-only" />
                          </IonButton>
                          <IonButton 
                            fill="clear"
                            color="danger"
                            onClick={() => confirmDisconnect(account.email)}
                          >
                            <IonIcon icon={trashOutline} slot="icon-only" />
                          </IonButton>
                        </div>
                      </IonItem>
                      <IonItemOptions side="end">
                        <IonItemOption color="danger" onClick={() => confirmDisconnect(account.email)}>
                          <IonIcon icon={trashOutline} slot="icon-only" />
                        </IonItemOption>
                      </IonItemOptions>
                    </IonItemSliding>
                  ))}
                </IonList>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <IonButton 
                    fill="outline" 
                    color="primary" 
                    expand="block"
                    onClick={handleConnect}
                  >
                    <IonIcon icon={addCircleOutline} slot="start" />
                    Add Another Google Account
                  </IonButton>

                  <IonButton 
                    fill="solid" 
                    color="primary" 
                    expand="block"
                    onClick={() => handleFetchEmails()}
                    disabled={fetchingEmails}
                  >
                    <IonIcon icon={mailOutline} slot="start" />
                    {fetchingEmails ? 'Fetching...' : 'Fetch Emails from All Accounts'}
                  </IonButton>

                  {connectedAccounts.length > 0 && (
                    <IonButton 
                      fill="outline" 
                      color="danger" 
                      expand="block"
                      onClick={() => confirmDisconnect()}
                    >
                      <IonIcon icon={unlinkOutline} slot="start" />
                      Disconnect All Accounts
                    </IonButton>
                  )}
                </div>
              </>
            )}
          </div>
        </IonCardContent>
      </IonCard>

      {/* Recent Emails */}
      {emails.length > 0 && (
        <IonCard>
          <IonCardHeader>
            <IonCardTitle className="flex items-center space-x-2">
              <IonIcon icon={mailOutline} className="text-xl" />
              <span>Recent Emails ({emails.length})</span>
            </IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {emails.map((email, index) => (
                <div 
                  key={email.id || index} 
                  className={`p-3 rounded-lg border ${
                    email.isTicketRelated 
                      ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20' 
                      : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-sm truncate flex-1 mr-2">
                      {email.subject}
                    </h4>
                    <div className="flex gap-2">
                      {email.accountEmail && (
                        <IonChip className="text-xs">
                          <IonLabel>{email.accountEmail}</IonLabel>
                        </IonChip>
                      )}
                      {email.isTicketRelated && (
                        <IonChip color="success" className="text-xs">
                          <IonLabel>Ticket Related</IonLabel>
                        </IonChip>
                      )}
                      {email.processed && (
                        <IonChip color="primary" className="text-xs">
                          <IonLabel>Sale Created</IonLabel>
                        </IonChip>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                    From: {email.from}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mb-2">
                    {new Date(email.date).toLocaleString()}
                  </p>
                  {email.body && (
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {email.body}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </IonCardContent>
        </IonCard>
      )}

      <IonAlert
        isOpen={showDisconnectAlert}
        onDidDismiss={() => {
          setShowDisconnectAlert(false);
          setAccountToDisconnect(null);
        }}
        header={accountToDisconnect ? `Disconnect ${accountToDisconnect}?` : "Disconnect All Google Accounts"}
        message={
          accountToDisconnect 
            ? `Are you sure you want to disconnect ${accountToDisconnect}? Email monitoring for this account will be disabled.`
            : "Are you sure you want to disconnect all Google accounts? Email monitoring will be completely disabled."
        }
        buttons={[
          {
            text: 'Cancel',
            role: 'cancel',
          },
          {
            text: 'Disconnect',
            role: 'destructive',
            handler: handleDisconnect,
          },
        ]}
      />

      <IonToast
        isOpen={showToast}
        onDidDismiss={() => setShowToast(false)}
        message={toastMessage}
        duration={3000}
        color={toastColor}
      />
    </>
  );
};

export default GoogleIntegration;